// src/components/explorers/wildfireExplorer/tabs/PerimeterUploadModal.tsx
import React, { useState } from 'react';
import AppModal from '../../../common/AppModal';
import { uploadPerimeter } from '../../../../services/perimeterApiService';
import {
  PERIMETER_SOURCE_OPTIONS,
  PERIMETER_SOURCE_NEEDS_DESCRIPTION,
  type PerimeterSourceKey,
} from '../../../../constants/perimeterSource';

interface PerimeterUploadModalProps {
  isOpen: boolean;
  fireNumber: string;
  onSuccess: () => void;
  onClose: () => void;
}

type DetectedFormat = 'gpx' | 'geojson' | 'shapefile' | null;

function detectFormat(filename: string): DetectedFormat {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'gpx') return 'gpx';
  if (ext === 'geojson' || ext === 'json') return 'geojson';
  if (ext === 'zip') return 'shapefile';
  return null;
}

export const PerimeterUploadModal: React.FC<PerimeterUploadModalProps> = ({
  isOpen,
  fireNumber,
  onSuccess,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [perimeterSource, setPerimeterSource] = useState<PerimeterSourceKey | ''>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsDescription = perimeterSource !== '' && PERIMETER_SOURCE_NEEDS_DESCRIPTION.has(perimeterSource);

  const detectedFormat: DetectedFormat = file ? detectFormat(file.name) : null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setError(null);
  };

  const resetForm = () => {
    setFile(null);
    setDescription('');
    setPerimeterSource('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      await uploadPerimeter(fireNumber, file, {
        description: description || undefined,
        sourceName: perimeterSource || undefined,
      });
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <AppModal isOpen={isOpen} onClose={handleCancel} width={480} persistent={uploading}>
      <div className="perimeter-upload-modal">
        <h3>Upload Perimeter</h3>
        <p className="upload-subtitle">Fire: {fireNumber}</p>

        <form onSubmit={handleSubmit} className="upload-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="perimeter-file">File <span className="required">*</span></label>
            <input
              id="perimeter-file"
              type="file"
              accept=".gpx,.geojson,.json,.zip"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {detectedFormat && (
              <span className="format-badge">{detectedFormat}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="perimeter-description">Description <span className="required">*</span></label>
            <input
              id="perimeter-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this perimeter"
              disabled={uploading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="perimeter-source">Perimeter Source <span className="required">*</span></label>
            <select
              id="perimeter-source"
              value={perimeterSource}
              onChange={(e) => setPerimeterSource(e.target.value as PerimeterSourceKey | '')}
              disabled={uploading}
              required
            >
              <option value="">Select source...</option>
              {PERIMETER_SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {needsDescription && (
              <p className="form-hint">Please specify the source in the Description field.</p>
            )}
          </div>

          <div className="form-actions btn-row">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!file || !perimeterSource || !description.trim() || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handleCancel}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  );
};
